import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../lib/icons";
import api from "../lib/api";
import { toast } from "sonner";
import { Modal, Field, Select } from "./ui-modal";
import { POST_STATUS, POST_PLATFORMS, POST_FORMATS, TIMEZONES, tzLabel, fmtDate } from "../lib/helpers";
import AuthImg from "./AuthImg";
import { useConfirm } from "./ConfirmDialog";

const emptyForm = (draft) => ({
  client_id: draft?.client_id || "",
  date: draft?.date || "",
  time: draft?.time || "",
  status: "a_valider",
  platforms: [],
  format: "post",
  theme: "",
  caption: "",
  preview_link_1_url: "", preview_link_1_name: "",
  preview_link_2_url: "", preview_link_2_name: "",
  tag_accounts: "",
  tags: [],
  timezone: "Europe/Paris",
  collaborator_mentions: [],
  notes: "",
});

export default function PostDetailModal({ mode, post, draft, clients = [], collaborators = [], onClose, onSaved }) {
  const { confirm } = useConfirm();
  const isAdmin = mode === "create" || mode === "edit";
  const [listes, setListes] = useState([]);
  const [f, setF] = useState(post ? {
    client_id: post.client_id, date: (post.date || "").slice(0, 10), time: post.time || "", status: post.status,
    platforms: post.platforms || [], format: post.format || "post", theme: post.theme || "",
    caption: post.caption || "",
    preview_link_1_url: post.preview_link_1_url || "", preview_link_1_name: post.preview_link_1_name || "",
    preview_link_2_url: post.preview_link_2_url || "", preview_link_2_name: post.preview_link_2_name || "",
    tag_accounts: post.tag_accounts || "", tags: post.tags || [], timezone: post.timezone || "Europe/Paris",
    collaborator_mentions: post.collaborator_mentions || [],
    notes: post.notes || "",
  } : emptyForm(draft));
  const [notes, setNotes] = useState(post?.notes || "");
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const togglePlatform = (p) => set("platforms", f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p]);
  // Les comptes identifiés sont RÉSOLUS PAR LE SERVEUR pour les trois espaces : l'entreprise et le
  // collaborateur n'ont pas l'annuaire, et ne doivent pas l'avoir.
  const comptesIdentifies = post?.tagged_accounts_view || [];
  // Les comptes identifiés, par canal. Un compte ne s'identifie que sur SON réseau, et un compte
  // sous consigne de non-interaction n'est jamais proposé.
  // L'équivalent UTC, calculé à l'écran : 09:00 à Paris et 09:00 à Dubaï ne sont pas la même heure,
  // et il faut le voir en écrivant, pas après coup.
  // Prévenir l'entreprise est un GESTE, jamais un effet de bord (décision de Mélilia du 27/10) :
  // créer une publication ne prévient plus personne, et mira.ia peut donc préparer sans atteindre
  // qui que ce soit. Et l'avis part UNE FOIS PAR MOIS (décision du 27/10) : le bouton suit donc
  // l'état du MOIS, pas de cette publication — sinon il se dirait actif sur la deuxième publication
  // de novembre alors qu'un clic n'enverrait rien.
  const [prevenu, setPrevenu] = useState(post?.month_notified_at || null);
  const [envoi, setEnvoi] = useState(false);
  // « d'octobre », pas « de octobre » : avril, août et octobre commencent par une voyelle.
  const duMois = (d) => {
    if (!d) return "de ce mois";
    const [y, m] = d.split("-").map(Number);
    const nom = new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(new Date(y, m - 1, 1));
    return /^[aeiouy]/i.test(nom) ? `d'${nom}` : `de ${nom}`;
  };
  const prevenir = async () => {
    // Mois déjà annoncé : un second avis reste possible — c'est la seule façon d'annoncer des
    // publications ajoutées plus tard dans le mois — mais il se demande, il ne s'attrape pas.
    if (prevenu && !(await confirm({
      title: "Envoyer un second avis ?",
      message: `Le calendrier ${duMois(f.date)} a déjà été annoncé le ${fmtDate(prevenu)}. `
             + "L'entreprise recevra un second avis « votre calendrier est prêt ».",
      confirmLabel: "Envoyer un second avis" }))) return;
    setEnvoi(true);
    try {
      const { data } = await api.post(`/admin/posts/${post.id}/prevenir${prevenu ? "?renvoyer=1" : ""}`);
      setPrevenu(data.month_notified_at || data.notified_at);
      toast.success(data.notice_sent === false
        ? `Le calendrier ${duMois(f.date)} avait déjà été annoncé : aucun avis envoyé`
        : "L'entreprise a été prévenue");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setEnvoi(false); }
  };

  const utcDe = (d, h, tz) => {
    if (!d || !h) return "—";
    try {
      // On cherche l'instant qui, LU DANS `tz`, affiche d/h. On mesure l'écart du fuseau à cette
      // date-là (l'heure d'été le fait varier), puis on le retire.
      const nu = new Date(`${d}T${h}:00Z`);
      const z = (f) => new Date(nu.toLocaleString("en-US", { timeZone: f })).getTime();
      return new Date(nu.getTime() - (z(tz || "Europe/Paris") - z("UTC"))).toISOString().slice(11, 16);
    } catch { return "—"; }
  };
  const tags = f.tags || [];
  const estTague = (res, cid) => tags.some((x) => x.network === res && x.account_id === cid);
  const geste = (res, cid) => (tags.find((x) => x.network === res && x.account_id === cid) || {}).kind || "tag";
  const comptesDe = (l, res) => (l.accounts || []).filter((c) => c.network === res && !c.blocked);
  const toggleCompte = (res, cid) => set("tags", estTague(res, cid)
    ? tags.filter((x) => !(x.network === res && x.account_id === cid))
    : [...tags, { network: res, account_id: cid, kind: "tag" }]);
  const setGeste = (res, cid, kind) => set("tags",
    tags.map((x) => (x.network === res && x.account_id === cid ? { ...x, kind } : x)));
  // Cocher une liste coche TOUS ses comptes de ce canal — c'est le geste courant ; on décoche
  // ensuite ce qu'on ne veut pas (décision de Mélilia du 27/10).
  const listeCochee = (l, res) => { const cs = comptesDe(l, res); return cs.length > 0 && cs.some((c) => estTague(res, c.id)); };
  const toggleListe = (l, res) => {
    const cs = comptesDe(l, res);
    if (listeCochee(l, res)) {
      const ids = cs.map((c) => c.id);
      set("tags", tags.filter((x) => !(x.network === res && ids.includes(x.account_id))));
    } else {
      const neufs = cs.filter((c) => !estTague(res, c.id)).map((c) => ({ network: res, account_id: c.id, kind: "tag" }));
      set("tags", [...tags, ...neufs]);
    }
  };
  const toggleMention = (cid) => set("collaborator_mentions", f.collaborator_mentions.includes(cid) ? f.collaborator_mentions.filter((x) => x !== cid) : [...f.collaborator_mentions, cid]);

  const save = async () => {
    if (!f.client_id) return toast.error("Sélectionnez une entreprise");
    if (!f.date) return toast.error("Sélectionnez une date");
    if (f.platforms.length === 0) return toast.error("Sélectionnez au moins une plateforme");
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post("/admin/posts", f);
        toast.success("Post créé");
      } else {
        const { client_id, ...upd } = f;
        await api.put(`/admin/posts/${post.id}`, upd);
        toast.success("Post enregistré");
      }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const uploadThumbnail = async (e) => {
    const file = e.target.files[0]; if (!file || !post) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/admin/posts/${post.id}/thumbnail`, fd); toast.success("Miniature ajoutée"); onSaved(); }
    catch { toast.error("Échec de l'upload"); }
  };

  const del = async () => {
    if (!(await confirm({ title: "Supprimer ce post ?", confirmLabel: "Supprimer", tone: "danger" }))) return;
    try { await api.delete(`/admin/posts/${post.id}`); toast.success("Post supprimé"); onSaved(); onClose(); }
    catch { toast.error("Erreur"); }
  };

  const review = async (status) => {
    if (status === "a_corriger" && !notes.trim()) return toast.error("Merci de préciser la correction attendue dans les notes");
    setSaving(true);
    try {
      await api.post(`/client/calendar/${post.id}/review`, { status, notes });
      toast.success(status === "valide" ? "Post validé" : "Correction demandée");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const st = post ? (POST_STATUS[post.status] || POST_STATUS.a_valider) : null;
  const cid = f.client_id || post?.client_id;
  useEffect(() => {
    let vivant = true;              // une seule réponse appliquée (mode strict de React)
    if (!isAdmin || !cid) { setListes([]); return undefined; }
    api.get(`/admin/clients/${cid}/tag-lists`).then((r) => { if (vivant) setListes(r.data); }).catch(() => {});
    return () => { vivant = false; };
  }, [isAdmin, cid]);

  const title = mode === "create" ? "Nouveau post" : mode === "edit" ? "Modifier le post" : "Détail du post";

  return (
    <Modal onClose={onClose} title={title} wide>
      <div className="space-y-4">
        {st && (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[9px] font-semibold border ${st.cls}`} data-testid="post-modal-status">
            {st.label}
          </span>
        )}

        {isAdmin && mode === "create" && (
          <Select label="Entreprise" value={f.client_id} onChange={(v) => set("client_id", v)}
            options={[{ v: "", l: "— Sélectionner —" }, ...clients.map((c) => ({ v: c.id, l: c.name }))]} testid="post-client" />
        )}
        {!isAdmin && post && (
          <div className="text-sm text-marine/80" data-testid="post-when">{fmtDate(post.date)}{post.time ? ` à ${post.time}${post.timezone ? ` (${tzLabel(post.timezone)})` : ""}${post.time_utc ? ` · ${post.time_utc} UTC` : ""}` : ""}</div>
        )}

        {post?.thumbnail_path && (
          <AuthImg path={post.thumbnail_path} alt="" className="w-full max-h-56 object-cover rounded-sm" />
        )}
        {isAdmin && post && (
          <Field label="Miniature">
            {/* `max-w-full` : un champ de fichier natif a une largeur propre et ne rétrécit pas —
                il sortait du cadre de la fenêtre de 29 px à 320 px (défaut antérieur au lot). */}
            <input type="file" accept="image/*" onChange={uploadThumbnail} data-testid="post-thumbnail" className="text-sm max-w-full" />
          </Field>
        )}

        {isAdmin ? (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Date"><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="post-date" className="inp" /></Field>
              <Field label="Heure de publication">
                <div className="flex flex-wrap gap-2">
                  <input type="time" value={f.time} onChange={(e) => set("time", e.target.value)} aria-label="Heure de publication" data-testid="post-time" className="inp w-32" />
                  <select value={f.timezone} onChange={(e) => set("timezone", e.target.value)} aria-label="Fuseau horaire"
                    data-testid="post-timezone" className="inp flex-1 min-w-[160px]">
                    {TIMEZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
                  </select>
                </div>
                <p className="text-xs text-marine/80 mt-1">
                  Facultative : sans heure, la publication s'affiche en haut de sa journée.
                  {f.time ? ` ${f.time} ${tzLabel(f.timezone)} = ${utcDe(f.date, f.time, f.timezone)} UTC.` : ""}
                </p>
              </Field>
            </div>
            <Field label="Statut">
              <select value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="post-status" className="inp">
                {Object.entries(POST_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Plateformes">
              <div className="flex flex-wrap gap-2">
                {Object.entries(POST_PLATFORMS).map(([k, p]) => {
                  const Ic = ICONS_BY_NAME[p.icon] || Icons.Share2;
                  const on = f.platforms.includes(k);
                  return (
                    <button type="button" key={k} onClick={() => togglePlatform(k)} data-testid={`post-platform-${k}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-marine text-white border-marine" : "bg-sable text-marine border-marine/20"}`}>
                      <Ic size={16} /> {p.label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Select label="Format" value={f.format} onChange={(v) => set("format", v)}
              options={Object.entries(POST_FORMATS).map(([k, v]) => ({ v: k, l: v.label }))} testid="post-format" />
            <Field label="Thématique de contenu"><input value={f.theme} onChange={(e) => set("theme", e.target.value)} data-testid="post-theme" className="inp" placeholder="Ex. Promotion été" /></Field>
            <Field label="Légende"><textarea value={f.caption} onChange={(e) => set("caption", e.target.value)} rows={3} data-testid="post-caption" className="inp w-full resize-y" /></Field>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Lien prévisualisation 1"><input value={f.preview_link_1_url} onChange={(e) => set("preview_link_1_url", e.target.value)} data-testid="post-link1-url" className="inp" placeholder="https://..." /></Field>
              <Field label="Nom du lien 1"><input value={f.preview_link_1_name} onChange={(e) => set("preview_link_1_name", e.target.value)} data-testid="post-link1-name" className="inp" placeholder="Ex. Canva" /></Field>
              <Field label="Lien prévisualisation 2"><input value={f.preview_link_2_url} onChange={(e) => set("preview_link_2_url", e.target.value)} data-testid="post-link2-url" className="inp" placeholder="https://..." /></Field>
              <Field label="Nom du lien 2"><input value={f.preview_link_2_name} onChange={(e) => set("preview_link_2_name", e.target.value)} data-testid="post-link2-name" className="inp" placeholder="Ex. Drive" /></Field>
            </div>
            <Field label="Comptes à identifier">
              {(f.platforms || []).length === 0 ? (
                <p className="text-sm text-marine/80">Choisissez d'abord un ou plusieurs réseaux ci-dessus : on n'identifie pas les mêmes comptes partout.</p>
              ) : listes.length === 0 ? (
                <p className="text-sm text-marine/80">Cette entreprise n'a aucune liste. Elles se créent sur la page de son module Social Media.</p>
              ) : (
                <div className="space-y-4" data-testid="post-tagged-accounts">
                  {f.platforms.map((res) => (
                    <div key={res} data-testid={`post-canal-${res}`}>
                      <div className="text-xs uppercase tracking-wider text-bordeaux mb-1.5">{POST_PLATFORMS[res]?.label || res}</div>
                      <div className="space-y-2">
                        {listes.map((l) => {
                          const tous = (l.accounts || []).filter((c) => c.network === res);
                          const libres = comptesDe(l, res);
                          const cochee = listeCochee(l, res);
                          return (
                            <div key={l.id} className="rounded-sm border border-marine/10 p-2">
                              <label className="flex flex-wrap items-center gap-2 text-sm">
                                <input type="checkbox" checked={cochee} disabled={libres.length === 0}
                                  onChange={() => toggleListe(l, res)} data-testid={`post-liste-${res}-${l.id}`} className="w-4 h-4 accent-marine" />
                                <span className={libres.length === 0 ? "text-marine/80" : "text-marine"}>{l.name}</span>
                                <span className="text-xs text-marine/80">
                                  {tous.length === 0 ? "aucun compte sur ce réseau" : `${libres.length} / ${tous.length}`}
                                </span>
                              </label>
                              {cochee && (
                                <div className="mt-2 pl-6 space-y-1.5">
                                  {tous.map((c) => {
                                    if (c.blocked) return (
                                      <div key={c.id} data-testid={`post-bloque-${res}-${c.id}`}
                                        title={`Ne rien taguer — ${c.blocked_reason || "sans raison notée"}`}
                                        className="text-sm text-rouge-texte line-through">{c.handle}</div>
                                    );
                                    const on = estTague(res, c.id);
                                    return (
                                      <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
                                        <label className="flex min-w-0 items-center gap-2">
                                          <input type="checkbox" checked={on} onChange={() => toggleCompte(res, c.id)}
                                            data-testid={`post-compte-${res}-${c.id}`} className="w-4 h-4 shrink-0 accent-marine" />
                                          {/* Jamais tronqué : on ne cache pas quel compte on coche. Il passe à la ligne. */}
                                          <span className="text-marine min-w-0 break-words">{c.handle}</span>
                                        </label>
                                        {on && (
                                          <select value={geste(res, c.id)} onChange={(e) => setGeste(res, c.id, e.target.value)}
                                            aria-label={`Geste pour ${c.handle}`} data-testid={`post-geste-${res}-${c.id}`}
                                            className="inp py-0.5 text-xs w-auto">
                                            <option value="tag">Tag</option>
                                            <option value="collaboration">Collaboration</option>
                                          </select>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Field>
            <Field label="Autre compte, ponctuel"><input value={f.tag_accounts} onChange={(e) => set("tag_accounts", e.target.value)} data-testid="post-tag-accounts" className="inp" placeholder="@invite-du-mois — hors liste, pour cette publication seulement" /></Field>
            <Field label="Identifications collaborateur">
              <div className="flex flex-wrap gap-2">
                {collaborators.map((c) => {
                  const on = f.collaborator_mentions.includes(c.id);
                  return (
                    <button type="button" key={c.id} onClick={() => toggleMention(c.id)} data-testid={`post-mention-${c.id}`}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-bordeaux text-white border-bordeaux" : "bg-white text-bordeaux border-bordeaux/30"}`}>
                      @{c.name}
                    </button>
                  );
                })}
                {collaborators.length === 0 && <span className="text-sm text-marine/80">Aucun collaborateur</span>}
              </div>
            </Field>
            <Field label="Notes / corrections"><textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} data-testid="post-notes-admin" className="inp w-full resize-y" /></Field>
            {/* `flex-wrap` : depuis que « Prévenir » s'est ajouté ici (27/10), la rangée porte TROIS
                boutons au lieu de deux, et « Enregistrer » sortait du cadre de la fenêtre de 43 px à
                320 px (mesuré contre la version de production, qui ne débordait pas). Même grammaire
                que la décision du 01/10 sur les boutons d'étape : ce qui ne tient pas côte à côte
                passe l'un sous l'autre. */}
            <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
              {mode === "edit" && (
                <button onClick={prevenir} disabled={envoi} data-testid="post-prevenir"
                  title={prevenu ? "Le calendrier de ce mois a déjà été annoncé — un nouveau clic enverra un second avis"
                                 : "Envoie l'avis à l'entreprise : un seul par mois"}
                  className={`text-sm rounded-sm px-4 py-2 border transition-colors disabled:opacity-60 ${
                    prevenu ? "border-marine/30 text-marine/80 hover:border-marine"
                            : "border-marine text-marine hover:bg-marine hover:text-white"}`}>
                  {prevenu ? `Prévenue le ${fmtDate(prevenu)}` : "Prévenir l'entreprise"}
                </button>
              )}
              {mode === "edit" ? <button onClick={del} data-testid="post-delete" className="text-sm text-rouge hover:underline inline-flex items-center gap-1"><Icons.Trash2 size={16} /> Supprimer</button> : <span />}
              <button onClick={save} disabled={saving} data-testid="post-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover disabled:opacity-50">{saving ? "Enregistrement..." : "Enregistrer"}</button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              {(post.platforms || []).map((p) => {
                const meta = POST_PLATFORMS[p]; const Ic = meta ? ICONS_BY_NAME[meta.icon] : Icons.Share2;
                return <span key={p} className="inline-flex items-center gap-1 text-sm text-marine/80"><Ic size={16} className="text-marine" /> {p}</span>;
              })}
              <span className="text-sm text-marine/80">· {(POST_FORMATS[post.format] || {}).label}</span>
            </div>
            {post.theme && <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Thématique</div><div className="text-sm">{post.theme}</div></div>}
            <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Légende</div><div className="text-sm whitespace-pre-wrap">{post.caption || "—"}</div></div>
            {(post.preview_link_1_url || post.preview_link_2_url) && (
              <div className="flex flex-wrap gap-3">
                {post.preview_link_1_url && <a href={post.preview_link_1_url} target="_blank" rel="noreferrer" data-testid="post-link1" className="text-sm text-marine hover:underline inline-flex items-center gap-1"><Icons.ExternalLink size={16} /> {post.preview_link_1_name || "Prévisualisation 1"}</a>}
                {post.preview_link_2_url && <a href={post.preview_link_2_url} target="_blank" rel="noreferrer" data-testid="post-link2" className="text-sm text-marine hover:underline inline-flex items-center gap-1"><Icons.ExternalLink size={16} /> {post.preview_link_2_name || "Prévisualisation 2"}</a>}
              </div>
            )}
            {(comptesIdentifies.length > 0 || post.tag_accounts) && (
              <div data-testid="post-comptes-lecture">
                <div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Comptes à identifier</div>
                {/* Groupés par canal : on n'identifie pas les mêmes comptes sur Instagram et sur
                    LinkedIn, et « collaboration » n'est pas « tag » — l'un invite à cosigner. */}
                {[...new Set(comptesIdentifies.map((c) => c.network))].map((res) => (
                  <div key={res} className="mb-1.5">
                    <span className="text-xs text-marine/80 mr-2">{POST_PLATFORMS[res]?.label || res}</span>
                    <span className="inline-flex flex-wrap gap-2 align-middle">
                      {comptesIdentifies.filter((c) => c.network === res).map((c, i2) => (
                        <span key={i2} title={c.handle} className={`inline-flex max-w-full items-center px-2.5 py-1 rounded-full text-sm border ${
                          c.kind === "collaboration" ? "bg-bordeaux-50 border-bordeaux/40 text-bordeaux" : "bg-sable border-marine/20 text-marine"}`}>
                          <span className="truncate min-w-0">{c.handle}</span>
                          {c.kind === "collaboration" ? <span className="ml-1.5 shrink-0 text-xs">collaboration</span> : null}
                        </span>
                      ))}
                    </span>
                  </div>
                ))}
                {post.tag_accounts && <div className="text-sm text-marine/80">Ponctuel : {post.tag_accounts}</div>}
              </div>
            )}

            {mode === "review" ? (
              <>
                <Field label="Notes / corrections"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="post-notes-client" placeholder="Précisez votre retour si vous demandez une correction..." className="inp w-full resize-y" /></Field>
                <div className="flex justify-end gap-2 pt-2">
                  <button onClick={() => review("a_corriger")} disabled={saving} data-testid="post-review-correct" className="border border-rouge text-rouge rounded-sm px-4 py-2 text-sm hover:bg-rouge hover:text-white transition-colors disabled:opacity-50">À corriger</button>
                  <button onClick={() => review("valide")} disabled={saving} data-testid="post-review-validate" className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover disabled:opacity-50">Valider</button>
                </div>
              </>
            ) : (
              post.notes && <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Notes / corrections</div><div className="text-sm whitespace-pre-wrap">{post.notes}</div></div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
